import { thetaApi, pageOf, pageLimit } from "./theta";
import type { EmojiUse, Page, PostReactions, Reactor } from "@theta/api-client";

/**
 * Set or swap the viewer's reaction — a singleton, so calling it twice with the
 * same emoji is one row.
 *
 * BOTH OF THESE ANSWER THE COMPLETE SUMMARY (`PostReactions.reactions`:
 * every emoji's count, whether it is mine, and the custom emoji's url). That
 * is the whole reason `usePostReaction` no longer carries 22 lines of
 * client-side count arithmetic: the authoritative shape comes back from the
 * write, so the optimistic state is RECONCILED against it rather than being
 * the only thing that ever knew the answer.
 */
export function setReaction(postId: number, emoji: string): Promise<PostReactions> {
  return thetaApi.setReaction(postId as PostReactions["postId"], emoji);
}

/** Remove it. 200 even when there was nothing to remove (QR8). */
export function removeReaction(postId: number): Promise<PostReactions> {
  return thetaApi.removeReaction(postId as PostReactions["postId"]);
}

/**
 * Who reacted, for the "who reacted" tooltip. Cursor-paged; the tooltip shows
 * at most ten names, so one page is the whole request.
 */
export function getReactors(postId: number, limit = 50): Promise<Page<Reactor>> {
  return thetaApi.http
    .GET("/posts/{id}/reactions", {
      params: { path: { id: postId }, query: { limit: pageLimit(limit) } },
    })
    .then(pageOf<Reactor>);
}

/**
 * `groups` is `maxLength: 500` in the contract, so a very long shared-group
 * list is truncated to whole ids rather than sent and refused. The row is
 * ordered by the viewer's own usage, so the groups that fall off the end are
 * the least likely to have contributed to it — and a slightly short scope
 * costs one emoji in a quick bar, where a 400 costs the whole row.
 */
const MAX_GROUPS_PARAM = 500;

function groupsParam(groupIds: readonly number[]): string {
  let value = "";
  for (const id of groupIds) {
    const next = value ? `${value},${id}` : String(id);
    if (next.length > MAX_GROUPS_PARAM) break;
    value = next;
  }
  return value;
}

/**
 * The current user's most-used emoji, most-used first — the reaction picker's
 * "frequently used" row.
 *
 * THE GROUP SCOPE IS THE PARAMETER THAT DECIDES WHETHER CUSTOM EMOJI APPEAR
 * AT ALL. `?groups=1,2` names the groups whose CUSTOM emoji may be in the
 * answer (§1.4 set membership), and the route's own description spells out
 * what omitting it means: "OMITTING it means standard emoji only, which is
 * what the DM picker needs before its shared groups have loaded". Server-side
 * that is literal — `parseGroupSet(undefined)` yields `[]`, and an EMPTY
 * `allowedCustom` narrows the query to `isNull(customEmojiUrl)`.
 *
 * So this is not the old `group_ids[]` leak guard being replaced by
 * server-side visibility; it is the only thing that lets a group's own `:tak:`
 * reach the row it is used in. Calling it with no groups asked the server for
 * standard emoji only, which is why a group that reacts with nothing else saw
 * six generic emoji it never uses.
 */
export function getEmojiFrequencies(
  groupIds: readonly number[] = [],
  limit = 20
): Promise<Page<EmojiUse>> {
  const groups = groupsParam(groupIds);
  return thetaApi.http
    .GET("/me/emoji-frequencies", {
      params: { query: { ...(groups ? { groups } : {}), limit: pageLimit(limit) } },
    })
    .then(pageOf<EmojiUse>);
}

export type { EmojiUse };
